import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DashboardExport } from "../components/Shared/ReportBuilder/dashboardExport";
import { ReportTemplate } from "../components/Shared/ReportBuilder/kpiTemplate";
import {
	DropTarget,
	MoveDirection,
	Placement,
	applyDrop,
	insertWidget,
	legacyOrder,
	moveWidget as moveWidgetInRows,
	normalizeRows,
	removeWidget as removeWidgetFromRows,
	resetToRows,
} from "../components/Shared/ReportBuilder/rowLayout";
import {
	ComparisonMode,
	DashboardRecipient,
	DashboardVisibility,
	NOTE_SPEC,
	ReportDashboard,
	ReportDateRange,
	ReportFilters,
	ReportRow,
	ReportShareInfo,
	ReportWidget,
	SharedDashboard,
	SharedDashboardSummary,
	StoredDashboard,
	WidgetDisplay,
	WidgetSpec,
} from "../models/ReportBuilder";

const HISTORY_LIMIT = 50;
const PROMPT_HISTORY_LIMIT = 20;

interface ReportBuilderStore {
	dashboards: ReportDashboard[];
	activeDashboardId?: string;
	/** Edit mode: drag and widget menus. Read mode is what a viewer sees. */
	editMode: boolean;
	/** TV mode (fullscreen report): the app shell hides its menus while on. Not persisted. */
	tvMode: boolean;
	/** Undo stack of the active report (not persisted). */
	past: ReportDashboard[];
	future: ReportDashboard[];
	setEditMode: (editMode: boolean) => void;
	setTvMode: (tvMode: boolean) => void;
	createDashboard: (
		name: string,
		init?: Partial<Pick<ReportDashboard, "range" | "compare">>
	) => string;
	/** Creates a report pre-filled from a template (widgets, period, comparison). */
	createFromTemplate: (template: ReportTemplate) => string;
	duplicateDashboard: (id: string) => string | undefined;
	/** Creates a private dashboard from pasted JSON (see dashboardExport), with fresh ids. */
	importDashboard: (data: DashboardExport) => string;
	renameDashboard: (id: string, name: string) => void;
	deleteDashboard: (id: string) => void;
	setActiveDashboard: (id: string) => void;
	setRange: (id: string, range: ReportDateRange) => void;
	setCompare: (id: string, compare?: ComparisonMode) => void;
	setFilters: (id: string, filters?: ReportFilters) => void;
	setAutoRefresh: (id: string, minutes?: number) => void;
	setShare: (id: string, share?: ReportShareInfo) => void;
	/** Who reaches the dashboard inside the app; the public link is `setShare`. */
	setVisibility: (id: string, visibility: DashboardVisibility) => void;
	/** The colleagues named on the dashboard; only honoured while visibility is `users`. */
	setSharedWith: (id: string, sharedWith: DashboardRecipient[]) => void;
	/**
	 * Ids this browser has seen on the server. What separates a dashboard that
	 * was deleted somewhere else (drop it) from one that only ever existed here
	 * and still has to be pushed up (keep it).
	 */
	syncedIds: string[];
	markSynced: (dashboardId: string) => void;
	/** Dashboards colleagues share with the organisation, as listed in the menu. */
	sharedDashboards: SharedDashboardSummary[];
	setSharedDashboards: (dashboards: SharedDashboardSummary[]) => void;
	/**
	 * Takes the server's copies as the truth for the user's own dashboards. One
	 * that is missing there is dropped if this browser has synced it before —
	 * it was deleted from another browser — and kept if it never reached the
	 * server, so it can be pushed up instead.
	 */
	adoptServerDashboards: (own: StoredDashboard[]) => void;
	/** Puts a colleague's dashboard in view, read-only and never persisted. */
	openSharedDashboard: (stored: StoredDashboard) => void;
	/** Adds a widget; it lands in a full-width row at the bottom unless a placement says otherwise. */
	addWidget: (
		dashboardId: string,
		widget: Omit<ReportWidget, "id" | "created">,
		placement?: Placement
	) => string;
	addNote: (dashboardId: string, text: string) => string;
	updateWidget: (
		dashboardId: string,
		widgetId: string,
		patch: Partial<Pick<ReportWidget, "spec" | "prompt" | "display" | "note" | "history">>
	) => void;
	/** New spec from a prompt; the previous generation goes to the widget's history. */
	regenerateWidget: (
		dashboardId: string,
		widgetId: string,
		spec: WidgetSpec,
		prompt: string
	) => void;
	setDisplay: (dashboardId: string, widgetId: string, patch: Partial<WidgetDisplay>) => void;
	duplicateWidget: (dashboardId: string, widgetId: string) => string | undefined;
	moveWidgetToDashboard: (fromId: string, widgetId: string, toId: string) => void;
	/** Applies a drop from the row renderer. */
	dropWidget: (dashboardId: string, target: DropTarget) => void;
	removeWidget: (dashboardId: string, widgetId: string) => void;
	/** Menu fallback for dragging: swap within the row, or into the row above/below. */
	moveWidget: (dashboardId: string, widgetId: string, direction: MoveDirection) => void;
	undo: () => void;
	redo: () => void;
}

const id = () =>
	typeof crypto !== "undefined" && "randomUUID" in crypto
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random()}`;

const updateDashboard = (
	dashboards: ReportDashboard[],
	dashboardId: string,
	fn: (d: ReportDashboard) => ReportDashboard
) => dashboards.map((d) => (d.id === dashboardId ? fn(d) : d));

/** Removes the one-level-per-report undo history bookkeeping from a state slice. */
type HistoryState = Pick<ReportBuilderStore, "dashboards" | "past" | "future">;

/**
 * Applies an edit to one report and records the previous version for undo.
 * Layout-only changes (drops) are recorded too; the stack is capped.
 */
function edit(
	s: HistoryState,
	dashboardId: string,
	fn: (d: ReportDashboard) => ReportDashboard,
	record = true
): Partial<HistoryState> {
	const before = s.dashboards.find((d) => d.id === dashboardId);
	const dashboards = updateDashboard(s.dashboards, dashboardId, fn);
	const after = dashboards.find((d) => d.id === dashboardId);
	if (!before || !after || before === after) return { dashboards };
	if (!record) return { dashboards };
	return { dashboards, past: [...s.past, before].slice(-HISTORY_LIMIT), future: [] };
}

/** Copies widgets with fresh ids and returns the rows rewritten to the new ids. */
const withCopiedWidgets = (
	source: Pick<ReportDashboard, "widgets" | "rows">,
	created: string
): Pick<ReportDashboard, "widgets" | "rows"> => {
	const ids = new Map(source.widgets.map((w) => [w.id, id()]));
	return {
		widgets: source.widgets.map((w) => ({ ...w, id: ids.get(w.id)!, created, history: undefined })),
		rows: source.rows.map((r) => ({
			id: id(),
			widgets: r.widgets.map((w) => ids.get(w)!).filter(Boolean),
		})),
	};
};

/** Keeps the rows in step with the widgets: one row per widget that lacks one, no ghosts. */
const withRows = (d: ReportDashboard, rows: ReportRow[]): ReportDashboard => {
	const next = normalizeRows(
		rows,
		d.widgets.map((w) => w.id)
	);
	return next === d.rows ? d : { ...d, rows: next };
};

/**
 * Versions 1–3 stored a size or a grid position per widget. Version 4 keeps
 * rows instead and resets every old report to one widget per row in reading
 * order (top to bottom, left to right); the grid rules are gone, so there is
 * nothing exact to convert to.
 */
function migrateToRows(state: any) {
	const dashboards = (state?.dashboards ?? []).map((d: any) => {
		const widgets = legacyOrder<any>(d.widgets ?? []).map(
			({ layout: _layout, size: _size, ...rest }: any) => rest
		);
		return { ...d, widgets, rows: resetToRows(widgets.map((w: any) => w.id)) };
	});
	return { ...state, dashboards, editMode: false };
}

/** The server-side shape of a report, for sharing. */
export const toSharedDashboard = (d: ReportDashboard): SharedDashboard => ({
	name: d.name,
	range: d.range,
	compare: d.compare,
	filters: d.filters,
	rows: d.rows,
	widgets: d.widgets.map((w) => ({
		id: w.id,
		spec: w.spec,
		...(w.display ? { display: w.display } : {}),
		...(typeof w.note === "string" ? { note: w.note } : {}),
	})),
});

/**
 * POC persistence: the user's report dashboards live in localStorage of this
 * browser. Moving them server-side (per user, per organisation) is a later
 * step; the shape is kept serialisable for that.
 */
export const useReportBuilderStore = create<ReportBuilderStore>()(
	persist(
		(set, get) => ({
			dashboards: [],
			sharedDashboards: [],
			syncedIds: [],
			activeDashboardId: undefined,
			editMode: false,
			tvMode: false,
			past: [],
			future: [],
			setEditMode: (editMode) => set({ editMode }),
			setTvMode: (tvMode) => set({ tvMode }),
			createDashboard: (name, init) => {
				const dashboard: ReportDashboard = {
					id: id(),
					name,
					range: init?.range ?? { preset: "this_year" },
					compare: init?.compare,
					widgets: [],
					rows: [],
					created: new Date().toISOString(),
				};
				set((s) => ({
					dashboards: [...s.dashboards, dashboard],
					activeDashboardId: dashboard.id,
					past: [],
					future: [],
				}));
				return dashboard.id;
			},
			duplicateDashboard: (dashboardId) => {
				const source = get().dashboards.find((d) => d.id === dashboardId);
				if (!source) return undefined;
				const created = new Date().toISOString();
				const dashboard: ReportDashboard = {
					...source,
					id: id(),
					name: `${source.name} (kopia)`,
					share: undefined,
					owner: undefined,
					visibility: "private",
					...withCopiedWidgets(source, created),
					created,
				};
				set((s) => ({
					dashboards: [...s.dashboards, dashboard],
					activeDashboardId: dashboard.id,
					past: [],
					future: [],
				}));
				return dashboard.id;
			},
			importDashboard: (data) => {
				const created = new Date().toISOString();
				const source = {
					widgets: data.widgets.map((w) => ({ ...w, created })),
					rows: data.rows,
				};
				const copied = withCopiedWidgets(source, created);
				const dashboard: ReportDashboard = {
					id: id(),
					name: data.name,
					range: data.range,
					compare: data.compare,
					autoRefreshMinutes: data.autoRefreshMinutes,
					widgets: copied.widgets,
					rows: normalizeRows(
						copied.rows,
						copied.widgets.map((w) => w.id)
					),
					created,
					visibility: "private",
				};
				set((s) => ({
					dashboards: [...s.dashboards, dashboard],
					activeDashboardId: dashboard.id,
					past: [],
					future: [],
				}));
				return dashboard.id;
			},
			createFromTemplate: (template) => {
				const created = new Date().toISOString();
				const widgets: ReportWidget[] = [];
				const rows: ReportRow[] = template.rows.map((row) => ({
					id: id(),
					widgets: row.map((w) => {
						const widget = { id: id(), spec: w.spec, prompt: w.prompt, created };
						widgets.push(widget);
						return widget.id;
					}),
				}));
				const dashboard: ReportDashboard = {
					id: id(),
					name: template.name,
					range: template.range,
					compare: template.compare,
					widgets,
					rows,
					created,
				};
				set((s) => ({
					dashboards: [...s.dashboards, dashboard],
					activeDashboardId: dashboard.id,
					past: [],
					future: [],
				}));
				return dashboard.id;
			},
			renameDashboard: (dashboardId, name) =>
				set((s) => edit(s, dashboardId, (d) => ({ ...d, name }))),
			deleteDashboard: (dashboardId) =>
				set((s) => {
					const dashboards = s.dashboards.filter((d) => d.id !== dashboardId);
					return {
						dashboards,
						syncedIds: s.syncedIds.filter((id) => id !== dashboardId),
						activeDashboardId:
							s.activeDashboardId === dashboardId ? dashboards[0]?.id : s.activeDashboardId,
					};
				}),
			setActiveDashboard: (dashboardId) =>
				set({ activeDashboardId: dashboardId, past: [], future: [] }),
			setRange: (dashboardId, range) => set((s) => edit(s, dashboardId, (d) => ({ ...d, range }))),
			setCompare: (dashboardId, compare) =>
				set((s) => edit(s, dashboardId, (d) => ({ ...d, compare }))),
			setFilters: (dashboardId, filters) =>
				set((s) =>
					edit(s, dashboardId, (d) => {
						const clean: ReportFilters = {};
						(Object.keys(filters ?? {}) as (keyof ReportFilters)[]).forEach((k) => {
							const values = filters?.[k]?.filter(Boolean);
							if (values?.length) clean[k] = values;
						});
						return { ...d, filters: Object.keys(clean).length ? clean : undefined };
					})
				),
			setAutoRefresh: (dashboardId, minutes) =>
				set((s) => edit(s, dashboardId, (d) => ({ ...d, autoRefreshMinutes: minutes }), false)),
			setShare: (dashboardId, share) =>
				set((s) => ({
					dashboards: updateDashboard(s.dashboards, dashboardId, (d) => ({ ...d, share })),
				})),
			setVisibility: (dashboardId, visibility) =>
				set((s) => ({
					dashboards: updateDashboard(s.dashboards, dashboardId, (d) => ({ ...d, visibility })),
				})),
			setSharedWith: (dashboardId, sharedWith) =>
				set((s) => ({
					dashboards: updateDashboard(s.dashboards, dashboardId, (d) => ({ ...d, sharedWith })),
				})),
			markSynced: (dashboardId) =>
				set((s) =>
					s.syncedIds.includes(dashboardId) ? s : { syncedIds: [...s.syncedIds, dashboardId] }
				),
			setSharedDashboards: (sharedDashboards) => set({ sharedDashboards }),
			adoptServerDashboards: (own) =>
				set((s) => {
					const fromServer = own.map((stored) => ({
						...stored.dashboard,
						id: stored.id,
						visibility: stored.visibility,
						sharedWith: stored.sharedWith,
						owner: undefined,
					}));
					const serverIds = new Set(fromServer.map((d) => d.id));
					// Only what this browser has never managed to store survives being
					// absent from the server; the rest was deleted by its owner, here or
					// in another browser, and must not come back to life.
					const unsent = s.dashboards.filter(
						(d) => !d.owner && !serverIds.has(d.id) && !s.syncedIds.includes(d.id)
					);
					const viewed = s.dashboards.filter((d) => d.owner);
					const dashboards = [...fromServer, ...unsent, ...viewed];
					const kept = new Set(dashboards.map((d) => d.id));
					return {
						dashboards,
						syncedIds: [...new Set([...s.syncedIds.filter((id) => kept.has(id)), ...serverIds])],
						activeDashboardId: kept.has(s.activeDashboardId ?? "")
							? s.activeDashboardId
							: dashboards[0]?.id,
					};
				}),
			openSharedDashboard: (stored) =>
				set((s) => {
					const dashboard: ReportDashboard = {
						...stored.dashboard,
						id: stored.id,
						visibility: stored.visibility,
						// Never sent for someone else's dashboard; make sure a stale
						// copy inside the document cannot leak it either.
						sharedWith: undefined,
						owner: { id: stored.ownerId, name: stored.ownerName },
					};
					return {
						dashboards: [...s.dashboards.filter((d) => d.id !== stored.id), dashboard],
						activeDashboardId: stored.id,
						past: [],
						future: [],
					};
				}),
			addWidget: (dashboardId, widget, placement) => {
				const widgetId = id();
				set((s) =>
					edit(s, dashboardId, (d) => ({
						...d,
						widgets: [...d.widgets, { ...widget, id: widgetId, created: new Date().toISOString() }],
						rows: insertWidget(d.rows, widgetId, placement),
					}))
				);
				return widgetId;
			},
			addNote: (dashboardId, text) =>
				get().addWidget(dashboardId, { spec: NOTE_SPEC, prompt: "", note: text }),
			regenerateWidget: (dashboardId, widgetId, spec, prompt) =>
				set((s) =>
					edit(s, dashboardId, (d) => ({
						...d,
						widgets: d.widgets.map((w) =>
							w.id === widgetId
								? {
										...w,
										spec,
										prompt,
										history: [
											...(w.history ?? []),
											{ prompt: w.prompt, spec: w.spec, at: new Date().toISOString() },
										].slice(-PROMPT_HISTORY_LIMIT),
								  }
								: w
						),
					}))
				),
			setDisplay: (dashboardId, widgetId, patch) =>
				set((s) =>
					edit(s, dashboardId, (d) => ({
						...d,
						widgets: d.widgets.map((w) =>
							w.id === widgetId ? { ...w, display: { ...(w.display ?? {}), ...patch } } : w
						),
					}))
				),
			duplicateWidget: (dashboardId, widgetId) => {
				const source = get()
					.dashboards.find((d) => d.id === dashboardId)
					?.widgets.find((w) => w.id === widgetId);
				if (!source) return undefined;
				const copyId = id();
				set((s) =>
					edit(s, dashboardId, (d) => ({
						...d,
						widgets: [
							...d.widgets,
							{ ...source, id: copyId, created: new Date().toISOString(), history: undefined },
						],
						rows: insertWidget(d.rows, copyId, { kind: "after", widgetId }),
					}))
				);
				return copyId;
			},
			moveWidgetToDashboard: (fromId, widgetId, toId) => {
				if (fromId === toId) return;
				const source = get()
					.dashboards.find((d) => d.id === fromId)
					?.widgets.find((w) => w.id === widgetId);
				if (!source) return;
				set((s) => {
					const removed = edit(s, fromId, (d) => ({
						...d,
						widgets: d.widgets.filter((w) => w.id !== widgetId),
						rows: removeWidgetFromRows(d.rows, widgetId),
					}));
					const dashboards = updateDashboard(removed.dashboards ?? s.dashboards, toId, (d) => ({
						...d,
						widgets: [...d.widgets, source],
						rows: insertWidget(d.rows, widgetId),
					}));
					return { ...removed, dashboards };
				});
			},
			undo: () =>
				set((s) => {
					const previous = s.past[s.past.length - 1];
					if (!previous) return s;
					const current = s.dashboards.find((d) => d.id === previous.id);
					return {
						dashboards: updateDashboard(s.dashboards, previous.id, () => previous),
						past: s.past.slice(0, -1),
						future: current ? [...s.future, current] : s.future,
					};
				}),
			redo: () =>
				set((s) => {
					const next = s.future[s.future.length - 1];
					if (!next) return s;
					const current = s.dashboards.find((d) => d.id === next.id);
					return {
						dashboards: updateDashboard(s.dashboards, next.id, () => next),
						future: s.future.slice(0, -1),
						past: current ? [...s.past, current] : s.past,
					};
				}),
			dropWidget: (dashboardId, target) =>
				set((s) => edit(s, dashboardId, (d) => withRows(d, applyDrop(d.rows, target)))),
			updateWidget: (dashboardId, widgetId, patch) =>
				set((s) =>
					edit(s, dashboardId, (d) => ({
						...d,
						widgets: d.widgets.map((w) => (w.id === widgetId ? { ...w, ...patch } : w)),
					}))
				),
			removeWidget: (dashboardId, widgetId) =>
				set((s) =>
					edit(s, dashboardId, (d) => ({
						...d,
						widgets: d.widgets.filter((w) => w.id !== widgetId),
						rows: removeWidgetFromRows(d.rows, widgetId),
					}))
				),
			moveWidget: (dashboardId, widgetId, direction) =>
				set((s) =>
					edit(s, dashboardId, (d) => withRows(d, moveWidgetInRows(d.rows, widgetId, direction)))
				),
		}),
		{
			name: "report-builder",
			version: 5,
			partialize: (s) => ({
				dashboards: s.dashboards.filter((d) => !d.owner),
				syncedIds: s.syncedIds,
				activeDashboardId: s.activeDashboardId,
				editMode: s.editMode,
			}),
			migrate: (state, version) => {
				let next = state as any;
				if (version < 4) next = migrateToRows(next);
				if (version < 5)
					// Everything already in this browser has been pushed to the server by
					// the sync that has run on every load since dashboards moved there.
					// Counting them as synced is what stops this browser from re-creating
					// dashboards the user deleted somewhere else.
					next = {
						...next,
						syncedIds: (next.dashboards ?? [])
							.filter((d: ReportDashboard) => !d.owner)
							.map((d: ReportDashboard) => d.id),
					};
				return next;
			},
		}
	)
);

export const useActiveDashboard = (): ReportDashboard | undefined => {
	const { dashboards, activeDashboardId } = useReportBuilderStore();
	return dashboards.find((d) => d.id === activeDashboardId) ?? dashboards[0];
};
